import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { HttpSharedCoreContext, HttpSharedFileContext } from '@shared/enums';
import { UserDetailsFacade } from '@shared/state/facades';
import { ReqType } from '@shared/utils';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';

@Injectable()
export class ClientHttpInterceptor implements HttpInterceptor {
  constructor(private userDetailsFacade: UserDetailsFacade) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.userDetailsFacade.query.userDetails$.pipe(
      take(1),
      switchMap((userDetails) => {
        const isAzADB2CCall = request.url.includes(
          environment.azureAdB2C.b2cPolicies.authorityDomain
        );

        if (isAzADB2CCall) {
          return next.handle(request);
        }

        if (request.headers.get('skipClientId')) {
          request = request.clone({
            headers: request.headers.delete('skipClientId'),
          });
          return next.handle(request);
        }

        // A caller that named its own client owns the whole address, so neither header is added
        // on top of it — a project from the selected client would contradict the one it named.
        if (request.headers.has('ClientId')) {
          return next.handle(request);
        }

        let clientId = userDetails?.selectedClientId;

        if (!clientId) {
          clientId =
            userDetails?.selectedSubproject?.project?.assignedClientId ||
            (userDetails?.projects || [{ assignedClientId: null }])[0]
              .assignedClientId ||
            userDetails?.clientsList[0]?.id;
        }

        // Files and core API calls never read a project or subproject — they authorize on the
        // bearer token alone and address content by path, not by tenant — so neither is added
        // outside dprApi, regardless of whether one is selected.
        const reqType = request.context.get(ReqType);
        const isDprApiCall =
          !Object.values(HttpSharedFileContext).includes(reqType) &&
          !Object.values(HttpSharedCoreContext).includes(reqType);

        // A caller that named its own subproject owns its project too, so the selected one is not
        // added on top of it — ClientId is still added, since a subproject never crosses a client,
        // so the selected client stays correct even while addressing a different subproject in it.
        const callerNamedSubproject = request.headers.has('SubprojectId');
        const subproject =
          isDprApiCall && !callerNamedSubproject
            ? userDetails?.selectedSubproject
            : undefined;

        // The selected subproject is the only project and subproject the session knows. It is
        // absent before the session lands and straight after a client switch, so the headers are
        // sent when there is one rather than always.
        const headers: { [name: string]: string } = {};

        if (clientId) {
          headers.ClientId = clientId;
        }

        if (subproject?.id) {
          headers.SubprojectId = String(subproject.id);
          if (subproject.project?.id) {
            headers.ProjectId = subproject.project.id;
          }
        }

        if (Object.keys(headers).length > 0) {
          request = request.clone({ setHeaders: headers });
        }
        return next.handle(request);
      })
    );
  }
}
